import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts/types';
import {
  DashboardFilterType,
  DashboardFilterValue,
  DataRange,
} from '@/client/reports';
import {
  PHISHING_DASHBOARD_VIEW,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types/locales.type';
import {
  AnalyticsHeader,
  KeyStatistics,
  PerformanceOverTime,
  PhishingAnalysis,
  PhishingAnalyticsEmptyState,
  PhishingVulnerableAnalysis,
} from '@/components/campaigns/analytics';
import { PhishingAnalyticsEmptyStateVariant } from '@/components/campaigns/analytics/PhishingAnalyticsEmptyState';
import { SimulationList } from '@/components/campaigns/SimulationList';
import { useCampaignStatusCounts } from '@/hooks/query/campaign-status-counts.hook';
import { useAppSelector } from '@/hooks/store';
import { useAccordionState } from '@/hooks/useAccordionState';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { FlexContainer } from '@/ui';
import { AccordionTabHeader } from '@/ui/accordion';
import { AppAccordion } from '@/ui/accordion/AppAccordion';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppChip } from '@/ui/chip';
import { dataRangeOptions } from '@/utils/helpers';
import { getHubspotIsActiveStatus } from '@/utils/hubspot/hubspot.helper';
import { AccordionTab, AccordionTabChangeEvent } from 'primereact/accordion';
import { Skeleton } from 'primereact/skeleton';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const pathItems = (t: TranslationFunctionType, account: Account) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('sidebar.analytics'),
    url: RedirectPaths[RedirectPathsEnum.PHISHING_ANALYTICS](),
    template: AppBreadCrumbTemplate,
  },
];

export const PhishingAnalyticsPage = () => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const activeIndexesKey = `${currentAccount?.id}-${currentUser?.id}`;
  const defaultIndexes = [0, 1, 2, 3, 4];
  const { activeIndex, handleActiveIndexChange: baseHandleActiveIndexChange } =
    useAccordionState({
      storageKey: PHISHING_DASHBOARD_VIEW,
      userKey: activeIndexesKey,
      defaultValue: defaultIndexes,
      multiple: true,
    });
  const [dataRange, setDataRange] = useState<DataRange>(dataRangeOptions(t)[0]);
  const [filterData, setFilterData] = useState<DashboardFilterValue>({
    type: DashboardFilterType.ALL_DATA,
    managers: [],
    departments: [],
    branches: [],
    groups: [],
  });

  const { campaignStatusCounts, isLoading: isStatusCountsLoading } =
    useCampaignStatusCounts({ accountId: currentAccount?.id });

  const emptyStateVariant =
    useMemo((): PhishingAnalyticsEmptyStateVariant | null => {
      if (!campaignStatusCounts) return null;

      const { completedCount, activeCount, scheduledCount } =
        campaignStatusCounts;

      if (completedCount > 0 || activeCount > 0) return null;

      if (scheduledCount > 0) return 'simulation-running';

      if (
        getHubspotIsActiveStatus(currentAccount?.hubspot?.autoPhishingStatus)
      ) {
        return 'auto-phishing';
      }

      if (currentAccount?.freeTrialEndsAt) return 'free-trial';

      return 'awt-customer';
    }, [campaignStatusCounts, currentAccount]);

  const getFilterData = (
    filterData: DashboardFilterValue,
    dataRange: DataRange,
  ) => {
    return {
      ...dataRange.value,
      managers: filterData?.managers?.map((manager) => manager.name),
      departments: filterData?.departments?.map(
        (department) => department.name,
      ),
      branches: filterData?.branches?.map((branch) => branch.id),
      groups: filterData?.groups?.map((group) => group.id),
    };
  };

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      <AppBreadCrumb model={pathItems(t, currentAccount as Account)} />
      <FlexContainer justify="flex-start" align="center" className="mb-4">
        <h1 className="mb-0">{t('phishing.analytics.title')}</h1>
        {currentAccount?.enableAiTrainingForVulnerableUsers ? (
          <AppChip
            size="xs"
            className="ml-4"
            label={t('phishing.analytics.title.chip')}
            type="primary"
            pulsate
          />
        ) : (
          <Link
            to={
              RedirectPaths[RedirectPathsEnum.ACCOUNT_TRAINING_SETTINGS]() +
              '#phishing'
            }
          >
            <AppChip
              size="xs"
              className="ml-4"
              label={t('phishing.analytics.aiTraining.disabled')}
              type="secondary"
            />
          </Link>
        )}
      </FlexContainer>

      {isStatusCountsLoading && (
        <Skeleton height="400px" borderRadius="var(--medium-border-radius)" />
      )}

      {!isStatusCountsLoading && emptyStateVariant && (
        <PhishingAnalyticsEmptyState variant={emptyStateVariant} />
      )}

      {!isStatusCountsLoading && !emptyStateVariant && (
        <>
          <AnalyticsHeader
            dataRange={dataRange}
            setDataRange={setDataRange}
            filterData={filterData}
            setFilterData={setFilterData}
          />

          <AppAccordion
            id="phishing-analytics"
            activeIndex={activeIndex}
            onTabChange={(e: AccordionTabChangeEvent) =>
              baseHandleActiveIndexChange(e)
            }
            multiple
          >
            <AccordionTab
              tabIndex={0}
              headerTemplate={
                <AccordionTabHeader
                  title={t('phishing.analytics.overTime')}
                  description={t('phishing.analytics.overTimeDescription')}
                />
              }
            >
              <PerformanceOverTime
                filters={getFilterData(filterData, dataRange)}
              />
            </AccordionTab>

            <AccordionTab
              tabIndex={1}
              headerTemplate={
                <AccordionTabHeader
                  title={t('phishing.analytics.keyStats')}
                  description={t('phishing.analytics.keyStatsDescription')}
                />
              }
            >
              <KeyStatistics filters={getFilterData(filterData, dataRange)} />
            </AccordionTab>

            <AccordionTab
              tabIndex={2}
              headerTemplate={
                <AccordionTabHeader
                  title={t('phishing.analytics.analysis')}
                  description={t('phishing.analytics.analysisDescription')}
                />
              }
            >
              <PhishingAnalysis
                filters={getFilterData(filterData, dataRange)}
              />
            </AccordionTab>

            <AccordionTab
              data-id="phishing-analytics-simulations"
              tabIndex={3}
              headerTemplate={
                <AccordionTabHeader
                  title={t('phishing.analytics.simulation')}
                  description={t('phishing.analytics.simulation.description')}
                />
              }
            >
              <SimulationList filters={getFilterData(filterData, dataRange)} />
            </AccordionTab>

            <AccordionTab
              tabIndex={4}
              headerTemplate={
                <AccordionTabHeader
                  title={t('phishing.simulation.usersOverview')}
                />
              }
            >
              <PhishingVulnerableAnalysis
                filters={getFilterData(filterData, dataRange)}
              />
            </AccordionTab>
          </AppAccordion>
        </>
      )}
    </FlexContainer>
  );
};
